import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Package, Truck, CheckCircle, Clock, MapPin, CreditCard } from 'lucide-react';

const statusSteps = [
  { key: 'pending', label: 'Pesanan Dibuat', icon: Clock },
  { key: 'paid', label: 'Pembayaran Diterima', icon: CreditCard },
  { key: 'processing', label: 'Diproses', icon: Package },
  { key: 'shipped', label: 'Dikirim', icon: Truck },
  { key: 'delivered', label: 'Diterima', icon: CheckCircle },
];

function getStepIndex(order) {
  if (order.status === 'delivered' || order.shipping_status === 'delivered') return 4;
  if (order.status === 'shipped' || order.shipping_status === 'in_transit' || order.tracking_number) return 3;
  if (order.status === 'processing') return 2;
  if (order.payment_status === 'paid') return 1;
  return 0;
}

export default function OrderTracking() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/customer/orders/${orderId}/tracking`)
      .then(r => setOrder(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId]);

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" /></div>;
  if (!order) return <div className="text-center py-16 text-slate-400">Pesanan tidak ditemukan</div>;

  const currentStep = getStepIndex(order);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/my/orders"><Button variant="ghost" size="icon"><ArrowLeft size={18} /></Button></Link>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Tracking Pesanan</h1>
          <p className="text-sm text-slate-500 font-mono">{order.order_number}</p>
        </div>
      </div>

      {/* Progress Steps */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between relative">
            {/* Line */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 z-0" />
            <div className="absolute top-5 left-0 h-0.5 bg-[var(--store-primary, #2D6CDF)] z-0 transition-all" style={{ width: `${(currentStep / (statusSteps.length - 1)) * 100}%` }} />
            {statusSteps.map((step, i) => {
              const Icon = step.icon;
              const done = i <= currentStep;
              const active = i === currentStep;
              return (
                <div key={step.key} className="relative z-10 flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${done ? 'bg-[var(--store-primary, #2D6CDF)] border-[var(--store-primary, #2D6CDF)] text-white' : 'bg-white border-slate-300 text-slate-400'} ${active ? 'ring-4 ring-blue-100' : ''}`}>
                    <Icon size={18} />
                  </div>
                  <p className={`text-xs mt-2 text-center ${done ? 'text-[var(--store-primary, #2D6CDF)] font-medium' : 'text-slate-400'}`}>{step.label}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Shipping Info */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Truck size={16} /> Info Pengiriman</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Kurir</span>
              <span className="font-medium">{order.shipping_courier || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">No. Resi</span>
              <span className="font-mono font-medium text-[var(--store-primary, #2D6CDF)]">{order.tracking_number || 'Belum tersedia'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Status Pengiriman</span>
              <Badge variant="outline">{order.shipping_status || order.status}</Badge>
            </div>
            {order.shipping_address && (
              <div className="pt-2 border-t border-slate-100">
                <p className="text-slate-500 flex items-center gap-1 mb-1"><MapPin size={12} /> Alamat Tujuan</p>
                <p className="text-slate-700">{typeof order.shipping_address === 'string' ? order.shipping_address : `${order.shipping_address.address || ''}, ${order.shipping_address.city || ''}`}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Summary */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Package size={16} /> Ringkasan Pesanan</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Status</span>
              <Badge className={order.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{order.payment_status}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Total</span>
              <span className="font-bold">Rp {fmt(order.total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Tanggal</span>
              <span>{new Date(order.created_at).toLocaleDateString('id-ID')}</span>
            </div>
            {(order.items || []).length > 0 && (
              <div className="pt-2 border-t border-slate-100 space-y-1">
                <p className="text-slate-500 font-medium">Items:</p>
                {order.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span>{item.product_name} x{item.quantity}</span>
                    <span>Rp {fmt(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
